import React, { useMemo } from 'react'
import {
  Box,
  Chip,
  Paper,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
} from '@mui/lab'
import {
  AccountBalance as AccountBalanceIcon,
  Assignment,
  Build,
  CheckCircle,
  DirectionsCar,
  Note as NoteIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  Schedule,
} from '@mui/icons-material'
import type { Vehicle } from '@otw/models'
import type { Contact } from '@otw/models/leads/contacts'
import { useContacts } from 'hooks/contacts/useContacts'
import { useContactCompanies } from 'hooks/contacts/useContactCompanies'
import { useLeads } from 'hooks/admin/useLeads'
import { useInventoryLeads } from 'hooks/admin/useInventoryLeads'
import { useUsers } from 'hooks/useUsers'
import { normalizeText } from 'views/SuperAdmin/Management/LeadManagement/util/customerUtils'
import {
  buildContactMergedTimelineEntries,
  contactMergedTimelineEntryAuthorId,
} from 'views/SuperAdmin/Management/LeadManagement/util/contactMergedTimeline'
import { activityAuthorDisplayName } from 'views/SuperAdmin/Management/LeadManagement/util/activityDisplay'
import {
  buildVehicleTimelineAllRows,
  type LogisticsTimelineRowForAll,
  type TitleTimelineRowForAll,
  type VehicleTimelineAllRow,
} from './vehicleTimelineAllEntries'

const SOURCE_LABEL: Record<VehicleTimelineAllRow['source'], string> = {
  logistics: 'Logistics',
  title: 'Title',
  customer: 'Customer',
}

const SOURCE_COLOR: Record<
  VehicleTimelineAllRow['source'],
  'default' | 'primary' | 'secondary' | 'success' | 'info' | 'warning'
> = {
  logistics: 'info',
  title: 'success',
  customer: 'secondary',
}

function findContactForVehicle(contacts: Contact[] | undefined, vehicle: Vehicle): Contact | null {
  if (!contacts?.length) return null
  const candidates = [vehicle.sold_to_user, vehicle.selling_to_user]
  for (const buyer of candidates) {
    if (!buyer) continue
    if (buyer.id) {
      const byUserId = contacts.find((c) => c.userId && c.userId === buyer.id)
      if (byUserId) return byUserId
    }
    const nameKey = normalizeText(buyer.name)
    if (nameKey) {
      const byName = contacts.find((c) => normalizeText(c.name) === nameKey)
      if (byName) return byName
    }
  }
  return null
}

function formatRowDate(sortMs: number): string {
  return new Date(sortMs).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function getTitleStatusIcon(label: string) {
  const lower = label.toLowerCase()
  if (lower.includes('paid') || lower.includes('payment')) return <PaymentIcon fontSize="small" />
  if (lower.includes('bank')) return <AccountBalanceIcon fontSize="small" />
  if (lower.includes('ship') || lower.includes('transit')) return <DirectionsCar fontSize="small" />
  if (lower.includes('complete') || lower.includes('received')) return <CheckCircle fontSize="small" />
  if (lower.includes('flip') || lower.includes('title')) return <Build fontSize="small" />
  return <Schedule fontSize="small" />
}

function rowIcon(row: VehicleTimelineAllRow) {
  if (row.source === 'customer') return <PersonIcon fontSize="small" />
  if (row.source === 'title') return getTitleStatusIcon(row.title)
  if (row.title.toLowerCase().includes('note')) return <NoteIcon fontSize="small" />
  if (row.title.toLowerCase().includes('paid') || row.title.toLowerCase().includes('fund')) {
    return <PaymentIcon fontSize="small" />
  }
  return <DirectionsCar fontSize="small" />
}

export interface TimelineAllTabProps {
  vehicle: Vehicle
  logisticsEntries: LogisticsTimelineRowForAll[]
  titleSteps: TitleTimelineRowForAll[]
  formatTimeAgo: (sortMs: number) => string
}

const TimelineAllTab: React.FC<TimelineAllTabProps> = ({
  vehicle,
  logisticsEntries,
  titleSteps,
  formatTimeAgo,
}) => {
  const theme = useTheme()
  const { contacts } = useContacts()
  const { companies: contactCompanies } = useContactCompanies()
  const { leads } = useLeads()
  const { inventoryLeads } = useInventoryLeads()
  const { users } = useUsers()

  const contact = useMemo(() => findContactForVehicle(contacts, vehicle), [contacts, vehicle])

  const customerEntries = useMemo(() => {
    if (!contact) return []
    const memberCompanies = contactCompanies.filter((company) =>
      company.members.some((m) => m.contactId === contact.id),
    )
    return buildContactMergedTimelineEntries({
      contact,
      memberCompanies,
      leads: leads ?? [],
      inventoryLeads: inventoryLeads ?? [],
    })
  }, [contact, contactCompanies, leads, inventoryLeads])

  const mergedRows = useMemo(
    () =>
      buildVehicleTimelineAllRows({
        logisticsEntries,
        titleSteps,
        customerEntries,
      }),
    [logisticsEntries, titleSteps, customerEntries],
  )

  if (mergedRows.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          No timeline events yet.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Logistics, title, and customer activity will appear here in chronological order.
        </Typography>
      </Box>
    )
  }

  return (
    <Timeline position="right">
      {mergedRows.map((row, index) => {
        const authorName =
          row.source === 'customer'
            ? activityAuthorDisplayName(users, contactMergedTimelineEntryAuthorId(row.entry))
            : null
        const chipColor = SOURCE_COLOR[row.source]

        return (
          <TimelineItem key={row.key}>
            <TimelineOppositeContent sx={{ m: 'auto 0' }} align="right" variant="body2" color="text.secondary">
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {formatRowDate(row.sortMs)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatTimeAgo(row.sortMs)}
                </Typography>
              </Box>
            </TimelineOppositeContent>
            <TimelineSeparator>
              <TimelineDot
                color={chipColor === 'default' ? 'grey' : chipColor}
                variant={row.source === 'customer' ? 'filled' : 'outlined'}
              >
                {rowIcon(row)}
              </TimelineDot>
              {index < mergedRows.length - 1 && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent sx={{ py: '12px', px: 2 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor:
                    row.source === 'customer'
                      ? 'secondary.main'
                      : row.source === 'title'
                        ? 'success.main'
                        : 'divider',
                  borderRadius: 2,
                  maxWidth: 420,
                  bgcolor:
                    row.source === 'customer'
                      ? alpha(theme.palette.secondary.main, theme.palette.mode === 'dark' ? 0.12 : 0.05)
                      : undefined,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.5 }}>
                  <Chip label={SOURCE_LABEL[row.source]} size="small" color={chipColor} sx={{ fontWeight: 600 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {row.title}
                  </Typography>
                </Box>
                {'detail' in row && row.detail ? (
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {row.detail}
                  </Typography>
                ) : null}
                {authorName ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    By {authorName}
                  </Typography>
                ) : null}
              </Paper>
            </TimelineContent>
          </TimelineItem>
        )
      })}
    </Timeline>
  )
}

export default TimelineAllTab
